import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../../contexts/AuthContext';
import ModalBase from '../ModalBase';

// Painel das ferramentas que aparecem no checklist de atendimento.
//
//   Acrescentar  -> entra no checklist (reativa se ja existiu com esse nome)
//   Substituir   -> troca por desgaste/quebra/perda: MOTIVO OBRIGATORIO
//   Remover      -> sai do checklist; o historico continua guardado
//
// Tudo fica no historico com autor e horario. Ferramenta e so conferencia:
// nao mexe no estoque.

const ACAO_LABEL = {
  adicionada: 'Acrescentada',
  substituida: 'Substituída',
  removida: 'Removida',
  reativada: 'Acrescentada de novo',
};

function dataHora(dt) {
  return dt ? new Date(dt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

function ModalSubstituir({ ferramenta, motivos, onClose, onSalvo }) {
  const [tipo, setTipo] = useState('desgaste');
  const [motivo, setMotivo] = useState('');
  const [nomeNovo, setNomeNovo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (motivo.trim().length < 5) {
      setErro('Descreva o motivo da substituição (ex.: lâmina sem corte, cabo trincado).');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await axios.post(`${API}/insumos/ferramentas/${ferramenta.id}/substituir`, {
        motivo_tipo: tipo, motivo: motivo.trim(), nome_novo: nomeNovo.trim() || null,
      });
      onSalvo();
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao registrar a substituição.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalBase titulo={`Substituir — ${ferramenta.nome}`} onClose={onClose} largura={540}>
      <form onSubmit={submit} className="portal-form">
        <p className="content-card__hint" style={{ marginTop: 0 }}>
          Registre por que a ferramenta está sendo trocada. O motivo é obrigatório e fica no histórico.
        </p>
        <label className="portal-field">
          <span>Motivo da substituição *</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {motivos.map((m) => <option key={m.chave} value={m.chave}>{m.label}</option>)}
          </select>
        </label>
        <label className="portal-field" style={{ marginTop: 12 }}>
          <span>Descreva o {tipo === 'desgaste' ? 'desgaste' : 'que aconteceu'} *</span>
          <textarea
            rows={3} autoFocus value={motivo} maxLength={500}
            onChange={(e) => { setMotivo(e.target.value); setErro(''); }}
            placeholder="Ex.: lâmina sem corte, cabo trincado, ponta espanada..."
          />
        </label>
        <label className="portal-field" style={{ marginTop: 12 }}>
          <span>Nome da nova ferramenta (opcional)</span>
          <input
            value={nomeNovo} maxLength={100} onChange={(e) => setNomeNovo(e.target.value)}
            placeholder={`Deixe em branco para manter "${ferramenta.nome}"`}
          />
        </label>
        {erro ? <div className="feedback-banner feedback-banner--error" style={{ marginTop: 12 }}>{erro}</div> : null}
        <div className="portal-form-actions" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn-portal btn-portal--outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-portal btn-portal--primary" disabled={salvando}>
            {salvando ? 'Registrando...' : 'Registrar substituição'}
          </button>
        </div>
      </form>
    </ModalBase>
  );
}

export default function PainelFerramentas({ onMudou }) {
  const [ferramentas, setFerramentas] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [nova, setNova] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [substituindo, setSubstituindo] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const [f, h] = await Promise.all([
        axios.get(`${API}/insumos/ferramentas`),
        axios.get(`${API}/insumos/ferramentas/historico`),
      ]);
      setFerramentas(f.data.data.ferramentas || []);
      setMotivos(f.data.data.motivos || []);
      setHistorico(h.data.data || []);
      setErro('');
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível carregar as ferramentas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // O checklist le a lista de ferramentas do catalogo: avisa a pagina.
  const depoisDeMudar = async () => {
    await carregar();
    onMudou?.();
  };

  const acrescentar = async (e) => {
    e.preventDefault();
    if (nova.trim().length < 2) return;
    setSalvando(true);
    setErro('');
    try {
      await axios.post(`${API}/insumos/ferramentas`, { nome: nova.trim() });
      setNova('');
      await depoisDeMudar();
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao acrescentar a ferramenta.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (f) => {
    const r = await Swal.fire({
      title: `Remover "${f.nome}"?`,
      text: 'Ela deixa de aparecer nos checklists novos. O histórico continua guardado.',
      input: 'text',
      inputPlaceholder: 'Motivo (opcional)',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.post(`${API}/insumos/ferramentas/${f.id}/remover`, { motivo: r.value || null });
      await depoisDeMudar();
    } catch (err) {
      setErro(err.response?.data?.message || 'Falha ao remover a ferramenta.');
    }
  };

  const motivoLabel = (chave) => motivos.find((m) => m.chave === chave)?.label || chave;

  return (
    <div className="insumo-ck">
      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card">
        <div className="insumo-ck__secao-topo">
          <h3 className="insumo-ck__secao">
            Ferramentas do checklist <span className="cell-muted">({ferramentas.length})</span>
          </h3>
          <form className="insumo-ck__nova-ferramenta" style={{ marginTop: 0 }} onSubmit={acrescentar}>
            <input
              className="insumo-ck__texto" placeholder="Nome da ferramenta" value={nova} maxLength={100}
              onChange={(e) => setNova(e.target.value)}
            />
            <button type="submit" className="btn-portal btn-portal--primary" disabled={salvando || nova.trim().length < 2}>
              <i className="bi bi-plus-lg" /> Acrescentar
            </button>
          </form>
        </div>
        <p className="content-card__hint">
          São as ferramentas que o técnico confere antes de sair. Ferramentas não baixam do estoque.
        </p>

        {carregando ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando ferramentas...</p>
          </div>
        ) : ferramentas.length ? (
          <div className="table-shell">
            <table className="portal-table">
              <thead><tr><th>Ferramenta</th><th>Substituições</th><th>Última substituição</th><th>Ações</th></tr></thead>
              <tbody>
                {ferramentas.map((f) => (
                  <tr key={f.id}>
                    <td><span className="cell-strong">{f.nome}</span></td>
                    <td className="cell-muted">{f.substituicoes || 0}</td>
                    <td className="cell-muted">
                      {f.ultima_substituicao_em ? (
                        <>
                          {dataHora(f.ultima_substituicao_em)} · {motivoLabel(f.ultima_substituicao_tipo)}
                          <div className="insumo-ck__detalhe">{f.ultima_substituicao_motivo}</div>
                        </>
                      ) : 'Nunca substituída'}
                    </td>
                    <td>
                      <div className="table-actions estoque-acoes">
                        <button type="button" className="btn-portal btn-portal--outline" onClick={() => setSubstituindo(f)}>
                          <i className="bi bi-arrow-repeat" /> Substituir
                        </button>
                        <button type="button" className="btn-portal btn-portal--outline estoque-acoes__excluir" title="Remover do checklist" onClick={() => remover(f)}>
                          <i className="bi bi-trash" /> Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <strong>Nenhuma ferramenta cadastrada</strong>
            <span>Acrescente as ferramentas que devem aparecer no checklist.</span>
          </div>
        )}
      </section>

      <section className="content-card">
        <h3 className="insumo-ck__secao">Histórico de alterações</h3>
        {historico.length ? (
          <div className="table-shell">
            <table className="portal-table">
              <thead><tr><th>Quando</th><th>Ferramenta</th><th>O que houve</th><th>Motivo</th><th>Quem</th></tr></thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.id}>
                    <td className="cell-muted">{dataHora(h.created_at)}</td>
                    <td>
                      <span className="cell-strong">{h.ferramenta_nome}</span>
                      {h.nome_novo ? <div className="insumo-ck__detalhe">nova: {h.nome_novo}</div> : null}
                    </td>
                    <td>{ACAO_LABEL[h.acao] || h.acao}</td>
                    <td className="cell-muted">
                      {h.motivo_tipo ? <strong>{motivoLabel(h.motivo_tipo)}</strong> : null}
                      {h.motivo_tipo && h.motivo ? ' — ' : ''}{h.motivo || (h.motivo_tipo ? '' : '—')}
                    </td>
                    <td className="cell-muted">{h.autor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="cell-muted">Nenhuma alteração registrada ainda.</p>}
      </section>

      {substituindo ? (
        <ModalSubstituir
          ferramenta={substituindo}
          motivos={motivos}
          onClose={() => setSubstituindo(null)}
          onSalvo={() => { setSubstituindo(null); depoisDeMudar(); }}
        />
      ) : null}
    </div>
  );
}
